import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { navigationAddress } from "../../common/deepLinks";

// Made when this file loads, not when telemetry is switched on: the tracking wrapper in
// telemtryProvider.jsx takes the plugin while the files load, and keeps what it was given.
const reactPlugin = new ReactPlugin();
let appInsights = null;

// How far into a telemetry item an address is looked for: an error's stack frames sit this deep.
const AddressDepth = 6;

const cutAddressIn = (holder, address, cut, depth) => {
  Object.keys(holder ?? {}).forEach((key) => {
    const value = holder[key];

    if (typeof value === "string") {
      holder[key] = value.split(address).join(cut);
    } else if (value && typeof value === "object" && depth > 1) {
      cutAddressIn(value, address, cut, depth - 1);
    }
  });
};

/**
 * A telemetry item with the page's address cut down to where it points (its path and its tab),
 * wherever the item names it. The rest of an address is state a screen keeps there, which can be
 * text a user typed (the Logs search) or values out of a customer's logs, and none of that
 * belongs in telemetry. The SDK names the address in a page view, along with the address before
 * it, and in an unhandled error, as the place it happened.
 * @param {Object} item - The telemetry item, changed in place
 * @param {string} address - The page's full address when the item was made
 * @return {Object} - The same item
 */
export const withoutViewState = (item, address) => {
  const cut = navigationAddress(address);

  if (address && cut !== address) {
    cutAddressIn(item.baseData, address, cut, AddressDepth);
    cutAddressIn(item.data, address, cut, AddressDepth);
  }

  if (item.baseData?.refUri) {
    item.baseData.refUri = navigationAddress(item.baseData.refUri);
  }

  return item;
};

/**
 * Create the App Insights Telemetry Service
 * @return {{reactPlugin: ReactPlugin, initialize: Function}} - Object
 */
const createTelemetryService = () => {
  /**
   * Initialize the Application Insights class
   * @param {string} connectionString - Application Insights Connection String
   * @param {Object} browserHistory - client's browser history, supplied by the withRouter HOC
   * @return {void}
   */
  const initialize = (connectionString, browserHistory) => {
    if (!browserHistory) {
      throw new Error("Could not initialize Telemetry Service");
    }
    if (!connectionString) {
      throw new Error(
        "App Insights connection string key not provided in ./src/telemetry-provider.jsx"
      );
    }

    appInsights = new ApplicationInsights({
      config: {
        connectionString: connectionString,
        maxBatchInterval: 0,
        disableFetchTracking: false,
        enableAutoRouteTracking: true,
            extensions: [reactPlugin],
        enableCorsCorrelation: true,
        extensionConfig: {
          [reactPlugin.identifier]: {
            history: browserHistory,
          },
        },
      },
    });

    appInsights.loadAppInsights();
    // Before anything is sent: every item goes out without the state screens keep in the address
    appInsights.addTelemetryInitializer((item) => {
      withoutViewState(item, window.location.href);
    });
    appInsights.trackPageView();
  };

  return { reactPlugin, initialize };
};

export const ai = createTelemetryService();
export const getAppInsights = () => appInsights;
